import React, { useEffect, useState } from 'react';
import './styles.scss';
import Rail, { Tab } from './Rail';
import CharacterPage, { CharacterState } from './CharacterPage';
import FactionPage, { FactionMembersState, FactionNewsState, FactionPanelState } from './FactionPage';
import HelpPage from './HelpPage';
import OptionsPage, { AudioSettings } from './OptionsPage';
import { getStoredPalette, applyPalette } from '../../theme/palette';
import {
  getStoredDrunkPersistent,
  getStoredHungerPersistent,
  setStoredDrunkPersistent,
  setStoredHungerPersistent,
} from '../../theme/hudPrefs';
import { getStoredUserScale, applyUserScale } from '../../theme/uiScale';
import { getStoredNotificationVolume, uiSounds } from '../../lib/uiSounds';

const REDUCE_ANIM_STORAGE_KEY = 'skymp5-esc-menu-reduce-animations';

const PAGE_TEXT: Record<Tab, { title: string; lead: string }> = {
  personnage: { title: 'Personnage', lead: 'Qui tu es, ce que tu sais faire, ce que tu possèdes.' },
  faction: { title: 'Faction', lead: 'Ton allégeance, ses membres et ses décrets.' },
  aide: { title: 'Aide', lead: 'Les mécaniques du serveur, les touches et les commandes.' },
  options: { title: 'Options', lead: "Son, taille de l'interface et palette de couleurs." },
};

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('escMenu sendMessage', payload);
  }
};

const EscMenu: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState<Tab>('personnage');
  const [character, setCharacter] = useState<CharacterState | null>(null);
  const [factionMembers, setFactionMembers] = useState<FactionMembersState | null>(null);
  const [factionNews, setFactionNews] = useState<FactionNewsState | null>(null);
  const [newsPostResult, setNewsPostResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [factionPanel, setFactionPanel] = useState<FactionPanelState | null>(null);
  // Which of the player's factions the Faction tab shows (a city and a group).
  const [selectedFactionId, setSelectedFactionId] = useState<string | null>(null);
  const [audio, setAudio] = useState<AudioSettings | null>(null);
  const [portraitImage, setPortraitImage] = useState<string | null>(null);
  const [palette, setPalette] = useState<string>(getStoredPalette);
  const [uiScale, setUiScale] = useState<number>(getStoredUserScale);
  const [notificationVolume, setNotificationVolume] = useState<number>(getStoredNotificationVolume);
  const [reduceAnimations, setReduceAnimations] = useState<boolean>(() => {
    try { return window.localStorage.getItem(REDUCE_ANIM_STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [hungerPersistent, setHungerPersistent] = useState<boolean>(getStoredHungerPersistent);
  const [drunkPersistent, setDrunkPersistent] = useState<boolean>(getStoredDrunkPersistent);
  const [quitConfirmOpen, setQuitConfirmOpen] = useState(false);

  useEffect(() => {
    const showHandler = () => {
      setTab('personnage');
      setFactionMembers(null);
      setFactionNews(null);
      setNewsPostResult(null);
      setFactionPanel(null);
      setSelectedFactionId(null);
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);
    const characterHandler = (e: Event) => setCharacter((e as CustomEvent).detail as CharacterState);
    const membersHandler = (e: Event) => setFactionMembers((e as CustomEvent).detail as FactionMembersState);
    const newsHandler = (e: Event) => setFactionNews((e as CustomEvent).detail as FactionNewsState);
    const postResultHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { ok?: boolean; text?: string } | null;
      setNewsPostResult({ ok: !!detail?.ok, text: detail?.text || '' });
    };
    const factionPanelHandler = (e: Event) => setFactionPanel((e as CustomEvent).detail as FactionPanelState | null);
    const audioHandler = (e: Event) => setAudio((e as CustomEvent).detail as AudioSettings);
    const portraitHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { image?: string } | null;
      if (detail?.image) setPortraitImage(detail.image);
    };

    window.addEventListener('skymp5-client:showEscMenu', showHandler);
    window.addEventListener('skymp5-client:hideEscMenu', hideHandler);
    window.addEventListener('skymp5-client:escMenuCharacterState', characterHandler);
    window.addEventListener('skymp5-client:escMenuFactionMembers', membersHandler);
    window.addEventListener('skymp5-client:escMenuFactionNews', newsHandler);
    window.addEventListener('skymp5-client:escMenuPostNewsResult', postResultHandler);
    window.addEventListener('skymp5-client:escMenuAudioSettings', audioHandler);
    window.addEventListener('skymp5-client:escMenuPortrait', portraitHandler);
    window.addEventListener('skymp5-client:escMenuFactionPanelState', factionPanelHandler);
    return () => {
      window.removeEventListener('skymp5-client:showEscMenu', showHandler);
      window.removeEventListener('skymp5-client:hideEscMenu', hideHandler);
      window.removeEventListener('skymp5-client:escMenuCharacterState', characterHandler);
      window.removeEventListener('skymp5-client:escMenuFactionMembers', membersHandler);
      window.removeEventListener('skymp5-client:escMenuFactionNews', newsHandler);
      window.removeEventListener('skymp5-client:escMenuPostNewsResult', postResultHandler);
      window.removeEventListener('skymp5-client:escMenuAudioSettings', audioHandler);
      window.removeEventListener('skymp5-client:escMenuPortrait', portraitHandler);
      window.removeEventListener('skymp5-client:escMenuFactionPanelState', factionPanelHandler);
    };
  }, []);

  // The faction the Faction tab is on: the picked one, else the first.
  const activeFaction = character?.factions?.find((f) => f.id === selectedFactionId) ?? character?.factions?.[0] ?? null;
  const factionId = activeFaction?.id;
  const selectFaction = (id: string) => {
    if (id === activeFaction?.id) return;
    setSelectedFactionId(id);
    // Each faction has its own roster, news and panel: drop the cached ones so
    // the page (remounted by its key) asks for the new faction's.
    setFactionMembers(null);
    setFactionNews(null);
    setNewsPostResult(null);
    setFactionPanel(null);
  };

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const selectPalette = (id: string) => {
    setPalette(id);
    applyPalette(id);
  };

  const selectUiScale = (value: number) => {
    setUiScale(value);
    applyUserScale(value);
  };

  const selectNotificationVolume = (value: number) => {
    setNotificationVolume(value);
    uiSounds.setVolume(value);
  };

  const toggleReduceAnimations = (value: boolean) => {
    setReduceAnimations(value);
    try { window.localStorage.setItem(REDUCE_ANIM_STORAGE_KEY, value ? '1' : '0'); } catch { /* ignore */ }
  };

  const toggleHungerPersistent = (value: boolean) => {
    setHungerPersistent(value);
    setStoredHungerPersistent(value);
  };

  const toggleDrunkPersistent = (value: boolean) => {
    setDrunkPersistent(value);
    setStoredDrunkPersistent(value);
  };

  const confirmQuit = () => {
    setQuitConfirmOpen(false);
    send({ action: 'quitGame' });
  };

  const factions = character?.factions ?? [];

  return (
    <div
      className={'esc-menu' + (reduceAnimations ? ' esc-menu--reduced-motion' : '')}
      role="dialog"
      aria-modal="true"
      aria-label="Menu du jeu"
    >
      <div className="esc-panel">
        <Rail
          tab={tab}
          onChangeTab={setTab}
          characterName={character?.name || null}
          septims={character?.septims ?? null}
          onResume={close}
          onQuit={() => setQuitConfirmOpen(true)}
        />

        <main className="esc-content">
          <header className="esc-content__head">
            <div>
              <h1 className="esc-content__title">{PAGE_TEXT[tab].title}</h1>
              <p className="esc-content__lead">{PAGE_TEXT[tab].lead}</p>
            </div>

            {tab === 'faction' && factions.length > 1 && (
              <div className="esc-segments" role="tablist" aria-label="Faction affichée">
                {factions.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={activeFaction?.id === f.id}
                    className={'esc-segment' + (activeFaction?.id === f.id ? ' esc-segment--active' : '')}
                    onClick={() => selectFaction(f.id)}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            )}
          </header>

          <div className="esc-page">
            {tab === 'personnage' && <CharacterPage character={character} portraitImage={portraitImage} />}
            {tab === 'faction' && (
              <FactionPage
                key={activeFaction?.id ?? 'none'}
                factionName={activeFaction?.name ?? character?.faction?.name ?? null}
                ownRankName={factionMembers?.ownRankName ?? activeFaction?.rankName ?? character?.faction?.rankName ?? null}
                members={factionMembers}
                news={factionNews}
                postResult={newsPostResult}
                panel={factionPanel}
                onRequestMembers={() => send({ action: 'requestFactionMembers', factionId })}
                onRequestNews={() => send({ action: 'requestFactionNews', factionId })}
                onPostNews={(title, text) => send({ action: 'postNews', title, text, factionId })}
                onRequestPanel={() => send({ action: 'requestFactionPanel', factionId })}
                onPromote={(target) => send({ action: 'promote', target, factionId })}
                onDemote={(target) => send({ action: 'demote', target, factionId })}
                onKick={(target) => send({ action: 'kick', target, factionId })}
                onLeave={() => send({ action: 'leaveFaction', factionId })}
                onSetCharter={(text) => send({ action: 'setCharter', text, factionId })}
                onApproveHousing={(refrId) => send({ action: 'approveHousing', refrId })}
                onDenyHousing={(refrId) => send({ action: 'denyHousing', refrId })}
                onEvictHousing={(refrId) => send({ action: 'evictHousing', refrId })}
                onManageHousingChests={(refrId) => send({ action: 'manageHousingChests', refrId })}
                onSetHousingTax={(amount) => send({ action: 'setHousingTax', amount })}
                onCollectHousingTreasury={() => send({ action: 'collectHousingTreasury' })}
              />
            )}
            {tab === 'aide' && <HelpPage />}
            {tab === 'options' && (
              <OptionsPage
                palette={palette}
                reduceAnimations={reduceAnimations}
                hungerPersistent={hungerPersistent}
                drunkPersistent={drunkPersistent}
                audio={audio}
                uiScale={uiScale}
                notificationVolume={notificationVolume}
                onSelectPalette={selectPalette}
                onToggleReduceAnimations={toggleReduceAnimations}
                onToggleHungerPersistent={toggleHungerPersistent}
                onToggleDrunkPersistent={toggleDrunkPersistent}
                onOpenGameSettings={() => send({ action: 'openGameSettings' })}
                onSetVolume={(key, value) => {
                  setAudio((prev) => (prev ? { ...prev, [key]: value } : prev));
                  send({ action: 'setAudioVolume', key, value });
                }}
                onSetUiScale={selectUiScale}
                onSetNotificationVolume={selectNotificationVolume}
              />
            )}
          </div>
        </main>
      </div>

      {quitConfirmOpen && (
        <div className="esc-dialog-scrim" role="alertdialog" aria-modal="true" aria-labelledby="esc-quit-title">
          <div className="esc-dialog">
            <h2 className="esc-dialog__title" id="esc-quit-title">Quitter le jeu ?</h2>
            <p className="esc-prose">Ta partie sera fermée. Tu pourras te reconnecter quand tu veux.</p>
            <div className="esc-form__actions">
              <button type="button" className="esc-btn" onClick={() => setQuitConfirmOpen(false)}>
                Annuler
              </button>
              <button type="button" className="esc-btn esc-btn--danger" onClick={confirmQuit}>
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EscMenu;
